import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { ToastService } from '@ps/core-overlay';
import { EffectsBase } from '@ps/core-data-access';

import { OperatorSettingsService } from '../../../services/operator-settings.service';
import { OperatorSettingsActions } from '../actions/operator-settings.actions';

@Injectable()
export class OperatorSettingsEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(OperatorSettingsService);
  private readonly _toast = inject(ToastService);

  public readonly load$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.load),
      switchMap(() =>
        this._service.getAll().pipe(
          map((settings) => OperatorSettingsActions.loadSuccess({ settings })),
          catchError((err: unknown) =>
            of(OperatorSettingsActions.loadFailure({ error: String(err) })),
          ),
        ),
      ),
    ),
  );

  public readonly update$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.update),
      switchMap(({ id, data }) =>
        this._service.update(id, data).pipe(
          map((settings) =>
            OperatorSettingsActions.updateSuccess({ settings }),
          ),
          catchError((err: unknown) =>
            of(OperatorSettingsActions.updateFailure({ error: String(err) })),
          ),
        ),
      ),
    ),
  );

  public readonly uploadPSIDs$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.uploadPSIDs),
      switchMap(({ formData }) =>
        this._service.uploadPSIDs(formData).pipe(
          map((res) =>
            OperatorSettingsActions.uploadPSIDsSuccess({
              ok: res.whitelisted.ok + res.blacklisted.ok,
              error: res.whitelisted.error + res.blacklisted.error,
            }),
          ),
          catchError((err: unknown) =>
            of(
              OperatorSettingsActions.uploadPSIDsFailure({
                error: String(err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public readonly uploadUnicornQuotas$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.uploadUnicornQuotas),
      switchMap(({ file }) =>
        this._service.uploadUnicornQuotas(file).pipe(
          tap(() => this._toast.success('Uploaded Unicorn Quotas')),
          map(() => OperatorSettingsActions.uploadUnicornQuotasSuccess()),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.uploadUnicornQuotasFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly downloadUnicornMappings$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.downloadUnicornMappings),
      switchMap(() =>
        this._service.downloadUnicornMappings().pipe(
          map(() => OperatorSettingsActions.downloadUnicornMappingsSuccess()),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.downloadUnicornMappingsFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly uploadCrmId$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.uploadCrmId),
      switchMap(({ file }) =>
        this._service.uploadCrmIdFile(file).pipe(
          tap(() => this._toast.success('Uploaded CRM ID')),
          map(() => OperatorSettingsActions.uploadCrmIdSuccess()),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.uploadCrmIdFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly downloadUnicornTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.downloadUnicornTemplate),
      switchMap(() =>
        this._service.downloadUnicornTemplate().pipe(
          map(() => OperatorSettingsActions.downloadUnicornTemplateSuccess()),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.downloadUnicornTemplateFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly downloadCrmIdTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.downloadCrmIdTemplate),
      switchMap(() =>
        this._service.downloadCrmIdTemplate().pipe(
          map(() => OperatorSettingsActions.downloadCrmIdTemplateSuccess()),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.downloadCrmIdTemplateFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly uploadTrafficDensityFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.uploadTrafficDensityFile),
      switchMap(({ file, country, countryName }) =>
        this._service.uploadTrafficDensityFile(file, country).pipe(
          tap(() =>
            this._toast.success(
              `Uploaded traffic density file for ${countryName}`,
            ),
          ),
          map(() => OperatorSettingsActions.uploadTrafficDensityFileSuccess()),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.uploadTrafficDensityFileFailure({
                  error: error.message,
                }),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  public readonly downloadTrafficDensityTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.downloadTrafficDensityTemplate),
      switchMap(() =>
        this._service.downloadTrafficDensityTemplate().pipe(
          map(() =>
            OperatorSettingsActions.downloadTrafficDensityTemplateSuccess(),
          ),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.downloadTrafficDensityTemplateFailure({
                  error: error.message,
                }),
              ),
            {
              notify: true,
              customMessage:
                'Error: Unable to download the file. Please refresh and try again.',
            },
          ),
        ),
      ),
    ),
  );

  public readonly downloadTrafficDensityCurrentFile$ = createEffect(() =>
    this._actions$.pipe(
      ofType(OperatorSettingsActions.downloadTrafficDensityCurrentFile),
      switchMap(({ country }) =>
        this._service.downloadTrafficDensityCurrentFile(country).pipe(
          map(() =>
            OperatorSettingsActions.downloadTrafficDensityCurrentFileSuccess(),
          ),
          this.psCatchError(
            (error) =>
              of(
                OperatorSettingsActions.downloadTrafficDensityCurrentFileFailure(
                  { error: error.message },
                ),
              ),
            {
              notify: true,
              customMessage:
                'Error: Unable to download the file. Please refresh and try again.',
            },
          ),
        ),
      ),
    ),
  );
}
